/**
 * OKS-64 — Inertia prop shapes for the brand-side Creator Analytics
 * surface (`/crew/:id/analytics`).
 *
 * Mirrors the server-side `CreatorAnalytics::ReliabilityScore.Result`
 * and `CreatorAnalytics::Timeseries.Result` Struct shapes 1:1 — keys
 * are snake_case (Inertia JSON round-trip preserves the Ruby
 * serializer's keys).
 *
 * # Two-mode UI (UX Spec §3 / TRD K12)
 *
 * The page branches on the `(oauth_state, snapshot_count)` tuple:
 *
 *   - `oauth_state="active"` + `snapshot_count > 0`   → success
 *   - `oauth_state="active"` + `snapshot_count == 0`  → syncing
 *   - `oauth_state="refresh_needed"` + ANY count      → success w/ amber pill
 *   - `oauth_state="revoked"`  + ANY count            → frozen w/ red pill
 *   - `oauth_state="none"`     + ANY count            → no-OAuth empty
 *
 * The reliability section renders in BOTH modes (computed from
 * brand-private collab/submission data, independent of OAuth).
 */

// =========================================================================
// Shared whitelists — mirror `CreatorAnalytics::Filters` constants
// =========================================================================

export const WINDOW_VALUES = ["30d", "90d", "all"] as const
export type CreatorAnalyticsWindow = (typeof WINDOW_VALUES)[number]

// =========================================================================
// OAuth state discriminator (TRD K12)
// =========================================================================

export const OAUTH_STATES = [
  "active",
  "refresh_needed",
  "revoked",
  "none",
] as const
export type OauthState = (typeof OAUTH_STATES)[number]

// `TiktokLinkState` is a subset of `OauthState` matching the
// `TiktokLinkStateCue` component contract (only the "needs attention"
// values render a pill; `active` and `none` render no pill).
export type TiktokLinkState = "refresh_needed" | "revoked" | null

// =========================================================================
// Reliability band discriminator (TRD K9 / PRD §3)
// =========================================================================

export const RELIABILITY_BANDS = ["excellent", "good", "at_risk", "na"] as const
export type ReliabilityBand = (typeof RELIABILITY_BANDS)[number]

// =========================================================================
// Reliability Hash shape (TRD K8.1 — explicit n/d Integers per component)
// =========================================================================

export interface ReliabilityComponent {
  numerator: number
  denominator: number
  /** `null` iff `denominator === 0`. FE renders `—%`. */
  rate: number | null
}

export type ReliabilityComponentKey = "on_time" | "completion" | "approval"

export interface ReliabilityProps {
  /** `null` iff `na: true` (PRD §3 N/A floor). */
  score: number | null
  band: ReliabilityBand
  /** `true` iff `denominator_combined < 3` (PRD §3 lock). */
  na: boolean
  /** Sum of all three component denominators. Drives the N/A floor. */
  denominator_combined: number
  components: Record<ReliabilityComponentKey, ReliabilityComponent>
  window: CreatorAnalyticsWindow
}

// =========================================================================
// Time-series Hash shape (TRD K11 / K12)
// =========================================================================

export interface TimeseriesPoint {
  /** ISO 8601 date string ("YYYY-MM-DD"). */
  date: string
  /** `null` only on the engagement series for video_count==0 days. */
  value: number | null
}

export interface TimeseriesProps {
  oauth_state: OauthState
  /** ISO timestamp string OR null. FE computes "Last synced X ago"
   *  via `date-fns`. */
  last_synced_at: string | null
  follower_series: TimeseriesPoint[]
  engagement_series: TimeseriesPoint[]
  /** Snapshot count in window — drives the "syncing" sub-state
   *  derivation (active + 0 snapshots = syncing). */
  snapshot_count: number
}

// =========================================================================
// Identity header — minimal payload reused by the Analytics tab + the
// Show tab (extracted to a shared component in T10).
// =========================================================================

export interface MembershipIdentity {
  id: number
  /** CrewMembership.status — drives the status badge. */
  status: string
  /** TikTok username (lowercased). null for manual-only creators. */
  handle: string | null
  /** Display name preference: TikTok display_name → user.name. */
  display_name: string
  /** TikTok avatar URL or null. */
  avatar_url: string | null
  /** Single discriminator for the link-state cue. null = no pill. */
  tiktok_link_state: TiktokLinkState
  /** js-routes-emitted paths the FE consumes directly (no client-side
   *  helper round-trip). */
  crew_message_path: string
  overview_path: string
  analytics_path: string
}

// =========================================================================
// Top-level page props
// =========================================================================

export interface CreatorAnalyticsProps {
  membership: MembershipIdentity
  window: CreatorAnalyticsWindow
  /** UX Spec OQ-8 — single source of truth for the two-mode UI
   *  branching. Same value as `timeseries.oauth_state` (server
   *  duplicates for spec ergonomics). */
  oauth_state: OauthState
  reliability: ReliabilityProps
  timeseries: TimeseriesProps
}
